import Swal from "sweetalert2";
import { element14AuthToken } from "actions/element14Action";

// Element14 responses return the auth token under a few possible keys.
export const getElement14AuthTokenValue = (tokenRes) =>
  tokenRes?.data?.Authorization ||
  tokenRes?.data?.token ||
  tokenRes?.data?.access_token ||
  tokenRes?.data?.authToken ||
  tokenRes?.data;

// Common look for the retry dialogs across every Element14 step.
const retrySwalOptions = ({ title, message }) => ({
  title,
  text: message,
  icon: "error",
  showCancelButton: true,
  confirmButtonText: "Retry",
  cancelButtonText: "Cancel",
  confirmButtonColor: "#5F47C9",
  cancelButtonColor: "#6b7280",
});

// Element14 issues tokens whose `iat` is ~20 s in the future and refuses them
// (ERR_JWT_TOKEN_EXPIRED) until then. The API reports how long that is as
// `usable_in_seconds`, measured on the server's clock; wait it out here, with
// a visible countdown, before the token's first use.
const waitUntilTokenUsable = async (seconds) => {
  if (!(seconds > 0)) return;
  await Swal.fire({
    title: "Connecting to Element14",
    text: `Element14 activates a new session in about ${seconds} seconds…`,
    icon: "info",
    timer: seconds * 1000,
    timerProgressBar: true,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
};

/**
 * Fetch a fresh Element14 auth token. If the token API fails, show a Retry
 * dialog and try again until it succeeds or the user cancels.
 * Resolves only once the token is usable. Returns { ok, token }.
 */
export const fetchElement14AuthToken = async () => {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const tokenRes = await element14AuthToken();
    if (tokenRes?.status) {
      await waitUntilTokenUsable(Number(tokenRes?.data?.usable_in_seconds) || 0);
      return { ok: true, token: getElement14AuthTokenValue(tokenRes) };
    }

    const { isConfirmed } = await Swal.fire(
      retrySwalOptions({
        title: "Element14 Auth Failed",
        message: tokenRes?.message || "Could not get Element14 auth token.",
      }),
    );
    if (!isConfirmed) return { ok: false };
  }
};

/**
 * Run a single Element14 API step with token-based retry.
 *
 * `apiCall` receives the current auth token and must return the API response
 * ({ status, message, data }). On failure a Retry dialog is shown; when the user
 * clicks Retry a fresh auth token is fetched via the token API and passed into
 * the next `apiCall` attempt. Repeats until success or the user cancels.
 *
 * Returns { ok, res, token } where `token` is the (possibly refreshed) token so
 * the caller can carry it into the following step.
 */
export const runElement14Step = async ({
  token,
  apiCall,
  title = "Element14 Request Failed",
  fallbackMessage = "Something went wrong. Please retry.",
  resolveMessage,
}) => {
  let currentToken = token;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await apiCall(currentToken);
    if (res?.status) return { ok: true, res, token: currentToken };

    const message =
      (resolveMessage && resolveMessage(res)) || res?.message || fallbackMessage;

    const { isConfirmed } = await Swal.fire(retrySwalOptions({ title, message }));
    if (!isConfirmed) return { ok: false, res, token: currentToken, cancelled: true };

    // Retry: call the token API and use the fresh token in the next attempt.
    const tokenResult = await fetchElement14AuthToken();
    if (!tokenResult.ok) return { ok: false, res, token: currentToken, cancelled: true };
    currentToken = tokenResult.token;
  }
};
